/**
 * Hand-rolled SVG and div gauges from the design system list: LinearGauge
 * (a percentage), LimitBar (a value against its normal band), RulBar
 * (remaining useful life), HealthRing (the dashboard ring), ConfidenceBar.
 */
import { cn } from '@/lib/cn';
import type { StatusColour } from '../../types';
import { fmtDays } from '../lib/format';
import { healthColour } from '../lib/values';

const BAR_COLOUR: Record<StatusColour, string> = { green: 'bg-nominal', yellow: 'bg-caution', red: 'bg-warning' };
const TEXT_COLOUR: Record<StatusColour, string> = { green: 'text-nominal', yellow: 'text-caution', red: 'text-warning' };
const STROKE: Record<StatusColour, string> = { green: 'var(--color-nominal)', yellow: 'var(--color-caution)', red: 'var(--color-warning)' };

export function LinearGauge({ value, colour, label, className }: { value: number; colour?: StatusColour; label?: string; className?: string }) {
  const v = Math.max(0, Math.min(100, value));
  const c = colour ?? healthColour(v);
  return (
    <div className={cn('flex items-center gap-2', className)} role="meter" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-inset">
        <div className={cn('h-full rounded-full transition-[width] duration-300', BAR_COLOUR[c])} style={{ width: `${v}%` }} />
      </div>
      <span className={cn('w-9 text-right font-mono text-[11px] tabular', TEXT_COLOUR[c])}>{Math.round(v)}%</span>
    </div>
  );
}

/** A value on a bar that spans the normal band and 40 % beyond on either side; the band is drawn, the value is a tick. */
export function LimitBar({ value, low, high, colour, className }: { value: number; low: number; high: number; colour: StatusColour | null; className?: string }) {
  const span = high - low || 1;
  const min = low - span * 0.4;
  const max = high + span * 0.4;
  const pct = (x: number) => Math.max(0, Math.min(100, ((x - min) / (max - min)) * 100));
  const c = colour ?? 'green';
  return (
    <div className={cn('relative h-2 w-full rounded-full bg-inset', className)} title={`Normal ${low} to ${high}`}>
      <div className="absolute inset-y-0 rounded-full bg-nominal/25" style={{ left: `${pct(low)}%`, width: `${pct(high) - pct(low)}%` }} />
      <div className={cn('absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-full', BAR_COLOUR[c])} style={{ left: `calc(${pct(value)}% - 1px)` }} />
    </div>
  );
}

/** Remaining useful life: days left over the base lifetime, coloured by the report's thresholds (>365 green, >180 yellow, else red). */
export function RulBar({ days, baseDays, className }: { days: number; baseDays: number; className?: string }) {
  const colour: StatusColour = days > 365 ? 'green' : days > 180 ? 'yellow' : 'red';
  const v = Math.max(0, Math.min(100, (days / Math.max(1, baseDays)) * 100));
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-inset">
        <div className={cn('h-full rounded-full', BAR_COLOUR[colour])} style={{ width: `${v}%` }} />
      </div>
      <span className={cn('font-mono text-[11px] tabular', TEXT_COLOUR[colour])}>{fmtDays(days)}</span>
    </div>
  );
}

export function ConfidenceBar({ value, className }: { value: number; className?: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className={cn('flex items-center gap-1.5', className)} title={`${Math.round(v)}% confidence`}>
      <div className="h-1 w-12 overflow-hidden rounded-full bg-inset">
        <div className="h-full rounded-full bg-ink-2" style={{ width: `${v}%` }} />
      </div>
      <span className="font-mono text-[10px] tabular text-ink-3">{Math.round(v)}%</span>
    </div>
  );
}

/** SVG ring (r = 42 on a 100 box, as the previous dashboard) with the value in the centre. */
export function HealthRing({ value, label, size = 112, colour, className }: { value: number; label?: string; size?: number; colour?: StatusColour; className?: string }) {
  const v = Math.max(0, Math.min(100, value));
  const c = colour ?? healthColour(v);
  const r = 42;
  const circumference = 2 * Math.PI * r;
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)} style={{ width: size, height: size }} role="meter" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--color-inset)" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={STROKE[c]} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - v / 100)} className="transition-[stroke-dashoffset] duration-500" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-mono text-xl font-semibold tabular', TEXT_COLOUR[c])}>{Math.round(v)}%</span>
        {label && <span className="text-[10px] uppercase tracking-wider text-ink-3">{label}</span>}
      </div>
    </div>
  );
}
